import { useEffect, useState } from "react";
import { useHref } from "react-router";
import { getUserCredits } from "../utils/api";

function Header() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [credits, setCredits] = useState(null);
    const [isLoadingCredits, setIsLoadingCredits] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    try {
        
        useEffect(() => {
            const checkAuth = () => {
                const token = localStorage.getItem('token');
                if (token) {
                    setIsAuthenticated(true);
                    const userData = JSON.parse(localStorage.getItem('user') || '{}');
                    setUser(userData);
                    // Check if user is admin
                    if (userData.email === 'admin@admin.com') {
                        setIsAdmin(true);
                    }
                    
                    // Fetch user credits
                    fetchUserCredits(userData.id);
                } else {
                    setIsAuthenticated(false);
                    setUser(null);
                    setCredits(null);
                    setIsAdmin(false);
                }
            };
            
            checkAuth();
            window.addEventListener('storage', checkAuth);
            
            // Listen for credit update events
            window.addEventListener('creditUpdate', (e) => {
                if (e.detail && typeof e.detail.credits === 'number') {
                    setCredits(e.detail.credits);
                }
            });
            
            return () => {
                window.removeEventListener('storage', checkAuth);
                window.removeEventListener('creditUpdate', () => {});
            };
        }, []);
        
        const fetchUserCredits = async (userId) => {
            if (!userId) return;
            
            setIsLoadingCredits(true);
            try {
                const creditData = await getUserCredits();
                setCredits(creditData.credits);
            } catch (error) {
                console.error('Failed to fetch credits:', error);
            } finally {
                setIsLoadingCredits(false);
            }
        };
        
        const handleLogout = () => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/';
        };
        
        const toggleMenu = () => {
            setIsMenuOpen(!isMenuOpen);
            if (isUserMenuOpen) setIsUserMenuOpen(false);
        };
        
        const toggleUserMenu = () => {
            setIsUserMenuOpen(!isUserMenuOpen);
            if (isMenuOpen) setIsMenuOpen(false);
        };
        
        const closeMenus = () => {
            setIsMenuOpen(false);
            setIsUserMenuOpen(false);
        };
        
        const navigateTo = (path) => {
            closeMenus();
            window.location.href = path;
        };
        
        // Inline CreditDisplay component to avoid dependency issues
        const CreditDisplay = ({ credits, isLoading }) => {
            const [isAnimating, setIsAnimating] = useState(false);
            const prevCreditsRef = useHref(credits);
            
            useEffect(() => {
                // If credits changed and it's not the initial load
                if (prevCreditsRef.current !== null && prevCreditsRef.current !== credits) {
                    setIsAnimating(true);
                    const timer = setTimeout(() => {
                        setIsAnimating(false);
                    }, 1500);
                    return () => clearTimeout(timer);
                }
                prevCreditsRef.current = credits;
            }, [credits]);
            
            return (
                <div 
                    className={`flex items-center ${isAnimating ? 'bg-yellow-200 transition-colors duration-1000' : 'bg-gray-100'} rounded-full px-3 py-1`} 
                    data-name="credit-display"
                >
                    <i className={`fas fa-coins ${isAnimating ? 'text-yellow-600 animate-bounce' : 'text-yellow-500'} mr-2`}></i>
                    {isLoading ? (
                        <div className="h-4 w-8 bg-gray-200 animate-pulse rounded"></div>
                    ) : (
                        <span className={`font-medium ${isAnimating ? 'text-yellow-800' : ''}`}>
                            {credits} 點數
                        </span>
                    )}
                </div>
            );
        };
        
        return (
            <header className="bg-white border-b-2 border-black sticky top-0 z-40" data-name="header">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <div className="flex-shrink-0" data-name="logo">
                            <a href="/" className="flex items-center">
                                <span className="text-2xl font-bold">River</span>
                                <span className="text-sm ml-1 text-gray-500">AI</span>
                            </a>
                        </div>
                        
                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex space-x-8" data-name="desktop-nav">
                            <a 
                                href="/workflow" 
                                className="flex items-center px-3 py-2 text-gray-700 hover:text-black"
                                data-name="nav-workflow"
                            >
                                <i className="fas fa-tools mr-2"></i>
                                工具箱
                            </a>
                            <a 
                                href="/books" 
                                className="flex items-center px-3 py-2 text-gray-700 hover:text-black"
                                data-name="nav-books"
                            >
                                <i className="fas fa-book mr-2"></i>
                                電子書
                            </a>
                            {isAuthenticated && (
                                <a 
                                    href="/history" 
                                    className="flex items-center px-3 py-2 text-gray-700 hover:text-black"
                                    data-name="nav-history"
                                >
                                    <i className="fas fa-history mr-2"></i>
                                    轉換記錄
                                </a>
                            )}
                            {isAuthenticated && (
                                <a 
                                    href="/voices" 
                                    className="flex items-center px-3 py-2 text-gray-700 hover:text-black"
                                    data-name="nav-vooices"
                                >
                                    <i className="fas fa-microphone mr-2"></i>
                                    語音記錄
                                </a>
                            )}
                            {isAuthenticated && (
                                <a 
                                    href="/rss" 
                                    className="flex items-center px-3 py-2 text-gray-700 hover:text-black"
                                    data-name="nav-payment"
                                >
                                    <i className="fas fa-rss mr-2"></i>
                                    RSS訂閱
                                </a>
                            )}
                            {isAuthenticated && (
                                <a 
                                    href="/payment" 
                                    className="flex items-center px-3 py-2 text-gray-700 hover:text-black"
                                    data-name="nav-payment"
                                >
                                    <i className="fas fa-credit-card mr-2"></i>
                                    購買點數
                                </a>
                            )}
                        </nav>
                        
                        {/* User Menu (Desktop) */}
                        <div className="hidden md:flex items-center space-x-4" data-name="desktop-user-menu">
                            {isAuthenticated && (
                                <CreditDisplay 
                                    credits={credits} 
                                    isLoading={isLoadingCredits}
                                />
                            )}
                            
                            {isAuthenticated ? (
                                <div className="relative">
                                    <button
                                        onClick={toggleUserMenu}
                                        className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100"
                                        data-name="user-menu-button"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                            <span className="text-sm font-semibold">
                                                {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                            </span>
                                        </div>
                                        <span className="font-medium">{user?.name || '使用者'}</span>
                                        <i className={`fas fa-chevron-down transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}></i>
                                    </button>
                                    
                                    {isUserMenuOpen && (
                                        <div 
                                            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-2 border-black py-2"
                                            data-name="user-dropdown"
                                        >
                                            <a 
                                                href="/profile"
                                                className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                                                data-name="profile-link"
                                            >
                                                <i className="fas fa-user mr-2"></i>
                                                個人資料
                                            </a>
                                            <a 
                                                href="/history"
                                                className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                                                data-name="history-link"
                                            >
                                                <i className="fas fa-history mr-2"></i>
                                                轉換記錄
                                            </a>
                                            <a 
                                                href="/payment"
                                                className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                                                data-name="payment-link"
                                            >
                                                <i className="fas fa-credit-card mr-2"></i>
                                                購買點數
                                            </a>
                                            <a 
                                                href="/billing"
                                                className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                                                data-name="billing-link"
                                            >
                                                <i className="fas fa-file-invoice-dollar mr-2"></i>
                                                帳單管理
                                            </a>
                                            <a 
                                                href="/subscriptions"
                                                className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                                                data-name="subscriptions-link"
                                            >
                                                <i className="fas fa-sync-alt mr-2"></i>
                                                訂閱管理
                                            </a>
                                            <button
                                                onClick={handleLogout}
                                                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                                                data-name="logout-button"
                                            >
                                                <i className="fas fa-sign-out-alt mr-2"></i>
                                                登出
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex space-x-4" data-name="auth-buttons">
                                    <a 
                                        href="/auth" 
                                        className="btn btn-outline"
                                        data-name="login-button"
                                    >
                                        登入
                                    </a>
                                    <a 
                                        href="/auth?register=true" 
                                        className="btn btn-primary"
                                        data-name="register-button"
                                    >
                                        註冊
                                    </a>
                                </div>
                            )}
                        </div>
                        
                        {/* Mobile Menu Button */}
                        <div className="md:hidden flex items-center" data-name="mobile-menu-button">
                            {isAuthenticated && (
                                <div className="mr-4">
                                    <CreditDisplay 
                                        credits={credits} 
                                        isLoading={isLoadingCredits}
                                    />
                                </div>
                            )}
                            <button
                                onClick={toggleMenu}
                                className="p-2"
                                aria-label="Toggle menu"
                            >
                                <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
                            </button>
                        </div>
                    </div>
                    
                    {/* Mobile Menu */}
                    {isMenuOpen && (
                        <div className="md:hidden py-4 border-t" data-name="mobile-menu">
                            <div className="space-y-2">
                                <a 
                                    href="/workflow"
                                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                    data-name="mobile-nav-workflow"
                                >
                                    <i className="fas fa-tools mr-2"></i>
                                    工具箱
                                </a>
                                <a 
                                    href="/books"
                                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                    data-name="mobile-nav-books"
                                >
                                    <i className="fas fa-book mr-2"></i>
                                    電子書
                                </a>
                                {isAuthenticated && (
                                    <a 
                                        href="/history"
                                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                        data-name="mobile-nav-history"
                                    >
                                        <i className="fas fa-history mr-2"></i>
                                        轉換記錄
                                    </a>
                                )}
                                {isAuthenticated && isAdmin && (
                                    <a 
                                        href="/payment"
                                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                        data-name="mobile-nav-payment"
                                    >
                                        <i className="fas fa-credit-card mr-2"></i>
                                        購買點數
                                    </a>
                                )}
                                {isAuthenticated && isAdmin && (
                                    <a 
                                        href="/billing"
                                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                        data-name="mobile-nav-billing"
                                    >
                                        <i className="fas fa-file-invoice-dollar mr-2"></i>
                                        帳單管理
                                    </a>
                                )}
                                {isAuthenticated && isAdmin && (
                                    <a 
                                        href="/subscriptions"
                                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                        data-name="mobile-nav-subscriptions"
                                    >
                                        <i className="fas fa-sync-alt mr-2"></i>
                                        訂閱管理
                                    </a>
                                )}
                                {isAdmin && (
                                    <a 
                                        href="/admin/stripe"
                                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                        data-name="mobile-nav-admin-stripe"
                                    >
                                        <i className="fas fa-cog mr-2"></i>
                                        Stripe 設定
                                    </a>
                                )}
                                
                                {isAuthenticated ? (
                                    <div className="pt-2 border-t mt-2" data-name="mobile-user-section">
                                        <div className="px-4 py-2 flex items-center">
                                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                                <span className="text-sm font-semibold">
                                                    {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                                </span>
                                            </div>
                                            <span className="ml-2 font-medium">{user?.name || '使用者'}</span>
                                        </div>
                                        <a 
                                            href="/profile"
                                            className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                            data-name="mobile-profile-link"
                                        >
                                            <i className="fas fa-user mr-2"></i>
                                            個人資料
                                        </a>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                            data-name="mobile-logout-button"
                                        >
                                            <i className="fas fa-sign-out-alt mr-2"></i>
                                            登出
                                        </button>
                                    </div>
                                ) : (
                                    <div className="pt-2 border-t mt-2 px-4 flex flex-col space-y-2" data-name="mobile-auth-buttons">
                                        <a 
                                            href="/auth" 
                                            className="btn btn-outline w-full"
                                            data-name="mobile-login-button"
                                        >
                                            登入
                                        </a>
                                        <a 
                                            href="/auth" 
                                            className="btn btn-primary w-full"
                                            data-name="mobile-register-button"
                                        >
                                            註冊
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </header>
        );
    } catch (error) {
        console.error('Header component error:', error);
        reportError(error);
        return null;
    }
}

export default Header;